import { z } from 'zod/v3';
import { ActionTypeSchema, StepStatusSchema, StepTypeSchema, ScreenshotScopeSchema } from '../generated/enums';
import { CoordinateSetSchema, CoordinatesSchema, ElementRectSchema, ViewportInfoSchema } from '../dom/coordinates';
import { HtmlSnapshotTypeSchema } from '../generated/enums';
import { ScreenshotMetadataDTOSchema } from './screenshot.dto';
import { SelectorStrategiesSchema } from './selector-strategy.dto';
// Save step schema
export const StepDTOSchema = z.object({
  id: z.string(),
  flowId: z.string(),
  workspaceId: z.string(),
  stepType: StepTypeSchema,
  action: ActionTypeSchema,
  timestamp: z.string(),
  relativeTime: z.number().nullish(),
  url: z.string().nullish(),
  pageTitle: z.string().nullish(),
  elementType: z.string().nullish(),
  internalNodeId: z.string().nullish(),
  selector: z.string().nullish(), // Primary CSS selector (backward compatibility)
  selectorStrategies: SelectorStrategiesSchema.nullish(), // Multiple selector strategies
  isHidden: z.boolean().nullish(),
  domText: z.string().nullish(),
  domState: z.string().nullish(),
  snapshotType: HtmlSnapshotTypeSchema.nullish(),
  html: z.string().nullish(),
  value: z.string().nullish(),
  initialValue: z.string().nullish(),
  pauseBefore: z.number().nullish(),
  viewportCoordinates: CoordinateSetSchema.nullish(),
  pageCoordinates: CoordinateSetSchema.nullish(),
  viewportInfo: ViewportInfoSchema.nullish(),
  // Mouse Coordinates
  mousePosition: CoordinatesSchema.nullish(),
  screenshot: ScreenshotMetadataDTOSchema.nullish(),
  screenshotScope: ScreenshotScopeSchema.nullish(),
  // AI generated fields
  stepTitle: z.string().nullish(),
  stepDescription: z.string().nullish(),
  title: z.string().nullish(),
  guideAction: z.string().nullish()
});

export type StepDTO = z.infer<typeof StepDTOSchema>;

export const CreateStepDTOSchema = StepDTOSchema.omit({
  screenshot: true,
  workspaceId: true,
}).and(z.object({
  screenshotDataUrl: z.string().nullish(),
  screenshotUploadId: z.string().uuid().nullish(),
  screenshotTileCount: z.number().int().positive().nullish(),
  screenshotWidth: z.number().nullish(),
  screenshotHeight: z.number().nullish(),
  faviconUrl: z.string().nullish(),
  domStateId: z.string().nullish(),
  // === NEW FIELDS FOR AUDITING & AI ANALYSIS (backward compatible - all optional) ===
  // Post-action screenshot for before/after comparison
  screenshotAfterDataUrl: z.string().nullish(),
  screenshotAfterWidth: z.number().nullish(),
  screenshotAfterHeight: z.number().nullish(),
  // Timestamps for precise action timing
  screenshotTimestamp: z.number().nullish(),      // Pre-action screenshot timestamp
  screenshotAfterTimestamp: z.number().nullish(), // Post-action screenshot timestamp
  actionTimestamp: z.number().nullish(),          // When user action occurred
  // Click metadata
  clickedViaLabel: z.boolean().nullish(),         // Click was on label delegated to input
  // Layer-specific metadata (Modal/Dropdown handling)
  layerAction: z.enum(['open', 'select', 'close']).nullish(),
  layerKind: z.enum(['modal', 'dropdown', 'autocomplete']).nullish(),
  layerRole: z.string().nullish(),
  nativeSelect: z.boolean().nullish(),
  // Screenshot display preference for UI rendering
  // 'before' = show pre-action screenshot (default for most actions)
  // 'after' = show post-action screenshot
  // 'both' = show before/after comparison (for layer actions, navigation)
  screenshotDisplay: z.enum(['before', 'after', 'both']).nullish(),
}));
export type CreateStepDTO = z.infer<typeof CreateStepDTOSchema>;

export const UpdateStepDTOSchema = z.object({
  notes: z.string().nullish(),
  isHidden: z.boolean().nullish(),
  stepTitle: z.string().nullish(),
  stepDescription: z.string().nullish(),
  guideAction: z.string().nullish(),
  action: ActionTypeSchema.optional(),
  viewportCoordinates: CoordinateSetSchema.nullish(),
});

export type UpdateStepDTO = z.infer<typeof UpdateStepDTOSchema>;

// Encoded PNG tile budget: base64 is at most 8 MiB, below the 10 MiB ingress.
export const SCREENSHOT_TILE_BYTES = 6 * 1024 * 1024;

/** An immutable saved image version; the server resolves pixels from the authorized Step. */
export type SavedScreenshotReference = { flowId: string; url: string };
